import React, { useMemo } from 'react';
import { View, FlatList, Linking, Platform } from 'react-native';
import { useTheme } from '@emotion/react';
import ODSHomeBoxTile from '../../atoms/ODSTiles/HomeboxTile';
import getScreenType from '../../../utils/getScreenType';
import ODSTiles from '../../atoms/ODSTiles';
import ODSInsuranceTile from '../../atoms/ODSTiles/InsuranceTile';
import ODSRentReadyTile from '../../atoms/ODSTiles/RentReadyTile';
import HousingGatewayCard from '../HousingGatewayCard/index.web';
import { CALENDLY_HOME_TENANT_INSURANCE, CREDIT_SPRING, INCOME_PROTECTION_ELEOS, PROPERTY_CLEAN } from '../../../utils/helper';
import { ODSText } from '../../atoms/ODSText';
import ODSBrick from '../../atoms/ODSBrick';
import { ODSIcon } from '../../atoms/ODSIcon';
import ODSHomeIframe from '../../atoms/ODSTiles/ODSHomeIframe';

interface ODSDashboardTilesProps {
  tiles: any[];
  navigationRoutes: any[];
  navigation: any;
  rentReadyData: any[];
  renderTenancyTile: React.ReactNode;
  isFccUser: boolean;
  isLoginTypeTds: boolean;
  iFrameParams: any;
  homeBaseUrl?: string;
  onHomeTilePressed: () => void;
  housingGatewayPressed: () => void;
  onMarketplacePressed: () => void;
}

const ODSDashboardTiles = React.memo(({
  tiles,
  navigationRoutes,
  navigation,
  rentReadyData,
  renderTenancyTile,
  isFccUser,
  isLoginTypeTds,
  iFrameParams,
  homeBaseUrl,
  onHomeTilePressed,
  housingGatewayPressed,
  onMarketplacePressed
}: ODSDashboardTilesProps) => {
  const { colors } = useTheme();
  const layout = getScreenType();

  const getItem = (id: string) => {
    return navigationRoutes.find((item: any) => item.id === id);
  };

  const bricks = useMemo(() => [
    {
      id: 'TenancyDeposit',
      title: 'Protect your Deposit',
      subtitle: 'View and Manage your tenancy deposit with the TDS.',
      image: require('../../../images/icons/dashboard/deposit.png'),
      gradientColors: [colors.main.blue[200], colors.main.blue[100]],
      accentColor: colors.main.blue[500],
      redirects: () => {
        const item = getItem('TenancyDeposit');
        if (item?.route) navigation.navigate(item.route);
      }
    },
    {
      id: 'ContentInsurance',
      title: 'Contents Insurance',
      subtitle: "Insurance carefully created for tenants. Covers contents and liability.",
      // image: require('../../../images/icons/dashboard/safehouse.png'),
      gradientColors: [colors.main.blue[300], colors.main.blue[100]],
      accentColor: colors.main.blue[500],
      safehouse: true,
      redirects: () => {
        if (Platform.OS === 'web') {
          const win = typeof window !== 'undefined' ? (window as any) : null;
          win?.open(CALENDLY_HOME_TENANT_INSURANCE, '_blank');
        } else {
          Linking.openURL(CALENDLY_HOME_TENANT_INSURANCE);
        }
      }
    },
    {
      id: 'CreditBuilder',
      title: 'Build Your Credit',
      subtitle: 'Report your rent payments to help boost your credit score.',
      image: require('../../../images/icons/dashboard/credit.png'),
      gradientColors: [colors.main.purple[200], colors.main.purple[100]],
      accentColor: colors.main.purple[500],
      redirects: () => {
        const item = getItem('CreditBuilder');
        if (item?.route) navigation.navigate(item.route);
      }
    },
    {
      id: 'IncomeProtection',
      title: 'Income Protection',
      subtitle: "Continue building the home you love, uninterrupted. Income protection covers your rent and bills if illness, injury, or unexpected unemployment stops you from working.",
      image: require('../../../images/icons/dashboard/income.png'),
      gradientColors: [colors.main.pink[200], colors.main.pink[100]],
      accentColor: colors.main.pink[500],
      redirects: () => {
        if (Platform.OS === 'web') {
          const win = typeof window !== 'undefined' ? (window as any) : null;
          win?.open(INCOME_PROTECTION_ELEOS, '_blank');
        } else {
          Linking.openURL(INCOME_PROTECTION_ELEOS);
        }
      }
    },
    {
      id: 'CreditSpring',
      title: 'Need a little extra support?',
      subtitle: 'Creditspring™ the smarter way to borrow for those unexpected costs.',
      image: require('../../../images/icons/dashboard/deposit.png'),
      gradientColors: [colors.main.gold[200], colors.main.gold[100]],
      accentColor: colors.main.gold[500],
      redirects: () => {
        if (Platform.OS === 'web') {
          const win = typeof window !== 'undefined' ? (window as any) : null;
          win?.open(CREDIT_SPRING, '_blank');
        } else {
          Linking.openURL(CREDIT_SPRING);
        }
      }
    },
    {
      id: 'PreQualification',
      title: 'Rent Ready',
      subtitle: 'Be a more desirable tenant by using our pre-qualification tool.',
      image: require('../../../images/icons/dashboard/rentready.png'),
      gradientColors: [colors.main.salmon[200], colors.main.salmon[100]],
      accentColor: colors.main.salmon[500],
      redirects: () => {
        const item = getItem('PreQualification');
        if (item?.route) navigation.navigate(item.route);
      }
    },
    {
      id: 'InfoHub',
      title: 'Stay Informed',
      subtitle: 'How we support tenants with our infohub.',
      image: require('../../../images/icons/dashboard/infohub.png'),
      gradientColors: [colors.main.purple[200], colors.main.purple[100]],
      accentColor: colors.main.purple[500],
      redirects: () => {
        const item = getItem('InfoHub');
        if (item?.route) navigation.navigate(item.route);
      }
    },

    {
      id: 'Rewards',
      title: 'Get Rewarded',
      subtitle: 'Take advantage of tlyfe rewards.',
      image: require('../../../images/icons/dashboard/rewards.png'),
      gradientColors: [colors.main.pink[200], colors.main.pink[100]],
      accentColor: colors.main.pink[500],
      redirects: () => {
        const item = getItem('Rewards');
        if (item?.route) navigation.navigate(item.route);
      }
    },
    {
      id: 'ShareCentre',
      title: 'Document & Share Centre',
      subtitle: 'View and share your documents with agents and landlords in a secure way.',
      image: require('../../../images/icons/dashboard/doc.png'),
      gradientColors: [colors.main.gold[200], colors.main.gold[100]],
      accentColor: colors.main.gold[500],
      redirects: () => {
        const item = getItem('MyDocuments');
        if (item?.route) navigation.navigate(item.route);
      }
    },
  ], [navigation, iFrameParams, homeBaseUrl, onHomeTilePressed, isLoginTypeTds, colors, navigationRoutes]);


  const renderODSTiles = ({ item }: { item: any }) => {
    let componentToRender;

    switch (item.card) {
      case 'BillingBetter':
        componentToRender =
          null;
        break;
      case 'OpenFlow':
        break;
      case 'Insurance':
        componentToRender =
          null;
        break;
      case 'MoneyCenter':
        null;
        break;
      case 'MyDocuments':
        componentToRender = (
          <ODSTiles
            source={require('../../../images/dashboard/tiles/md.png')}
            title={'My Documents & Share Centre'}
            restStyle={{
              marginVertical: layout == 'phone' ? 10 : 15,
              marginHorizontal: layout == 'phone' ? 0 : 15,
            }}
            subtitle="View and share your documents with agents and landlords in a secure way."
            btnTheme={colors.main.purple}
            onPress={() => {
              navigation.navigate(getItem('MyDocuments').route);
            }}
          />
        );
        break;
      case 'CreditBuilder':
        componentToRender = (
          <ODSTiles
            isRight
            source={require('../../../images/dashboard/tiles/cb.png')}
            title={'Credit Builder'}
            restStyle={{
              marginVertical: layout == 'phone' ? 10 : 15,
              marginHorizontal: layout == 'phone' ? 0 : 15,
            }}
            subtitle="Report your rent payments to help boost your credit score."
            btnTheme={colors.main.green}
            onPress={() => {
              navigation.navigate(getItem('CreditBuilder').route);
            }}
          />
        );
        break;
      case 'TenancyDeposit':
        componentToRender = (
          <ODSTiles
            source={require('../../../images/dashboard/tiles/td.png')}
            title={'Tenancy Deposit'}
            restStyle={{
              marginVertical: layout == 'phone' ? 10 : 15,
              marginHorizontal: layout == 'phone' ? 0 : 15,
            }}
            subtitle="View and Manage your tenancy deposit with the TDS."
            btnTheme={colors.main.pink}
            onPress={() => {
              navigation.navigate(getItem('TenancyDeposit').route);
            }}
          />
        );
        break;
      case 'CreditSpring':
        componentToRender = (
          <ODSTiles
            source={require('../../../images/dashboard/tiles/td.png')}
            title={'Need a little extra support?'}
            restStyle={{
              marginVertical: layout == 'phone' ? 10 : 15,
              marginHorizontal: layout == 'phone' ? 0 : 15,
            }}
            subtitle="Creditspring™ the smarter way to borrow for those unexpected costs."
            btnTheme={colors.main.pink}
            onPress={() => {
              if (Platform.OS === 'web') {
                const win = typeof window !== 'undefined' ? (window as any) : null;
                win?.open(CREDIT_SPRING, '_blank');
              } else {
                Linking.openURL(CREDIT_SPRING);
              }
            }}
          />
        );
        break;
      // case 'PreQualification':
      //   componentToRender = (
      //     <View
      //       style={{
      //         marginVertical: layout == 'phone' ? 10 : 15,
      //         marginHorizontal: layout == 'phone' ? 0 : 15,
      //       }}>
      //       <ODSRentReadyTile
      //         onPress={() => navigation.navigate(getItem('PreQualification').route)}
      //         rentReadyData={rentReadyData}
      //       />
      //     </View>
      //   );
      //   break;
      // case 'IDVerification':
      //   componentToRender = (
      //     <ODSTiles
      //       isRight
      //       restStyle={{
      //         marginVertical: layout == 'phone' ? 10 : 15,
      //         marginHorizontal: layout == 'phone' ? 0 : 15,
      //       }}
      //       source={require('../../../images/dashboard/tiles/id.png')}
      //       title={'ID Verification'}
      //       subtitle="Unlock a world of convenience with tlyfe's verified digital ID, accepted at over 30,000 outlets across the UK."
      //       status={getItem('IDVerification').status}
      //       btnTheme={colors.main.purple}
      //       onPress={() => {
      //         navigation.navigate(getItem('IDVerification').route);
      //       }}
      //     />
      //   );
      //   break;
      // case 'ReferenceProgression':
      //   componentToRender = (
      //     <ODSTiles
      //       restStyle={{
      //         marginVertical: layout == 'phone' ? 10 : 15,
      //         marginHorizontal: layout == 'phone' ? 0 : 15,
      //       }}
      //       source={require('../../../images/dashboard/tiles/fcc.png')}
      //       title={'Reference Progression'}
      //       subtitle="The latest news & videos for tenants in the private rental sector."
      //       btnTheme={colors.main.green}
      //       onPress={() => {
      //         navigation.navigate('FccHome');
      //       }}
      //       topImage={require('../../../images/logos/fcc-logo.png')}
      //     />
      //   );
      //   break;
      case 'Rewards':
        componentToRender = (
          <ODSTiles
            restStyle={{
              marginVertical: layout == 'phone' ? 10 : 15,
              marginHorizontal: layout == 'phone' ? 0 : 15,
            }}
            source={require('../../../images/dashboard/tiles/tr.png')}
            title={'tlyfe Rewards'}
            subtitle="Access hundreds of savings with National Retailers, including Waitrose, M&S, Ikea, Sainsburys, Pizza Express and many, many more!"
            btnTheme={colors.main.pink}
            onPress={() => {
              navigation.navigate(getItem('Rewards').route);
            }}
          />
        );
        break;
      case 'InfoHub':
        componentToRender = (
          <ODSTiles
            restStyle={{ marginVertical: 10 }}
            isRight
            source={require('../../../images/dashboard/tiles/ih.png')}
            title={'Info Hub'}
            subtitle="The latest news & videos for tenants in the private rental sector."
            btnTheme={colors.main.purple}
            onPress={() => {
            }}
          />
        );
        null;
        break;
      case 'TlfyeProtect':
        componentToRender = (
          [<ODSTiles
            isRight={true}
            restStyle={{
              marginVertical: layout == 'phone' ? 10 : 15,
              marginHorizontal: layout == 'phone' ? 0 : 15,
            }}
            source={require('./../../../images/ip.png')}
            title={'Income Protection'}
            subtitle="Continue building the home you love, uninterrupted with income protection insurance. By protecting your income, you will be able to stay on top of rent and bills when you're too ill or injured to work."
            btnTheme={colors.main.pink}
            onPress={() => {
              Linking.openURL(INCOME_PROTECTION_ELEOS, "_blank")
            }}
          />,
          <ODSTiles
            isRight
            restStyle={{ marginBottom: 40, }}
            source={require('./../../../images/ci.png')}
            title={'Home Contents Insurance'}
            subtitle="Protect your belongings from life's surprises—get contents insurance and keep your valuables safe from theft, damage, or loss."
            btnTheme={colors.main.purple}
            onPress={() => {
              Linking.openURL(CALENDLY_HOME_TENANT_INSURANCE, "_blank")
            }}
          />
          ]
        );
        break;

      default:
        componentToRender = null;
        break;
    }
    return componentToRender;
  };
  const combinedData = [
    { card: 'ReferenceProgression' },
    ...tiles,
    { card: 'TlfyeProtect' }


  ];

  const isPhone = layout === 'phone';

  return (
    <View style={{ flex: 1 }}>

      <View style={{ height: 30 }} />

      {/* ── Page container ── */}
      <View style={{
        width: '100%',
        maxWidth: 1100,
        alignSelf: 'center',
        paddingHorizontal: isPhone ? 16 : 40,
      }}>

        {/* ── Header ── */}
        <View style={{ marginBottom: isPhone ? 20 : 32, marginTop: isPhone ? 0 : 8, alignItems: 'center' }}>
          <View style={{
            width: 40,
            height: 3,
            borderRadius: 2,
            backgroundColor: colors.button.main,
            marginBottom: 14,
          }} />
          <ODSText type={isPhone ? 'h5' : 'h4'} color={colors.text.black} align='center'>
            Improve your tenant wellbeing
          </ODSText>
          <ODSText type='sm' color={colors.text.grey} align='center' marginTop={6}>
            Explore services designed to support you throughout your tenancy
          </ODSText>
        </View>

        {/* ── Brick Grid ── */}
        {isPhone ? (
          // Mobile: single column stack
          <View style={{ width: '100%' }}>
            {bricks.map((brick) => (
              <View key={brick.id} style={{ marginBottom: 12 }}>
                <ODSBrick
                  title={brick.title}
                  subtitle={brick.subtitle}
                  image={brick.image}
                  onPress={brick.redirects}
                  nonChild={brick.id === "PropertyClean"}
                  safehouse={brick.safehouse}
                  gradientColors={brick.gradientColors}
                  accentColor={brick.accentColor}
                >
                  {brick.renderChild ? brick.renderChild() : null}
                </ODSBrick>
              </View>
            ))}
          </View>
        ) : (
          // Web: clean 2-column grid
          <View style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            width: '100%',
          }}>
            {bricks.map((brick, index) => {
              const isLastAndOdd = index === bricks.length - 1 && bricks.length % 2 !== 0;
              const isOdd = index % 2 === 1;
              return (
                <View key={brick.id} style={{
                  width: isLastAndOdd ? '100%' : '48.5%',
                  marginRight: (isOdd || isLastAndOdd) ? 0 : '3%',
                  marginBottom: 20,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <ODSBrick
                    title={brick.title}
                    subtitle={brick.subtitle}
                    image={brick.image}
                    onPress={brick.redirects}
                    nonChild={brick.id === "PropertyClean"}
                    safehouse={brick.safehouse}
                    gradientColors={brick.gradientColors}
                    accentColor={brick.accentColor}
                  >
                    {brick.renderChild ? brick.renderChild() : null}
                  </ODSBrick>
                </View>
              );
            })}
          </View>
        )}
      </View>

      {/* ── Housing Gateway ── */}
      <View style={{
        width: isPhone ? '90%' : '60%',
        maxWidth: 700,
        marginTop: 32,
        marginBottom: 16,
        alignSelf: 'center',
      }}>
        <HousingGatewayCard hasBorder housingGatewayPressed={housingGatewayPressed} />
      </View>
    </View>
  );
});

export default ODSDashboardTiles;